import { useEffect, useState } from 'react'
import { Edit2, Plus, Trash2, X } from 'lucide-react'

import {
  apiCreateVehicleBrand,
  apiDeleteVehicleBrand,
  apiGetVehicleBrands,
  apiUpdateVehicleBrand,
  type VehicleBrand,
} from '../../../../api/vehicleManagement/vehicleBrandApi'
import { useConfirmDialog } from '../../../../context/ConfirmDialogContext'

type VehicleBrandManagerProps = {
  refreshKey?: number
}

const emptyForm = {
  name: '',
  slug: '',
  is_active: true,
}

export const VehicleBrandManager = ({
  refreshKey = 0,
}: VehicleBrandManagerProps) => {
  const { confirm, showSuccess } = useConfirmDialog()
  const [brands, setBrands] = useState<VehicleBrand[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingBrand, setEditingBrand] = useState<VehicleBrand | null>(null)

  const [formData, setFormData] = useState(emptyForm)
  const [isSaving, setIsSaving] = useState(false)

  const loadBrands = async () => {
    setIsLoading(true)
    setError(null)

    const response = await apiGetVehicleBrands()

    if (response.success) {
      setBrands(response.data || [])
    } else {
      setError(response.message)
      setBrands([])
    }

    setIsLoading(false)
  }

  useEffect(() => {
    loadBrands()
  }, [refreshKey])

  const openCreateModal = () => {
    setEditingBrand(null)
    setFormData(emptyForm)
    setIsModalOpen(true)
  }

  const openEditModal = (brand: VehicleBrand) => {
    setEditingBrand(brand)

    setFormData({
      name: brand.name,
      slug: brand.slug,
      is_active: brand.is_active,
    })

    setIsModalOpen(true)
  }

  const closeModal = () => {
    if (isSaving) return

    setIsModalOpen(false)
    setEditingBrand(null)
    setFormData(emptyForm)
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Brand name is required.')
      return
    }

    setIsSaving(true)
    setError(null)

    const payload = {
      name: formData.name.trim(),
      slug: formData.slug.trim() || undefined,
      is_active: formData.is_active,
    }

    const response = editingBrand
      ? await apiUpdateVehicleBrand(editingBrand.id, payload)
      : await apiCreateVehicleBrand(payload)

    if (!response.success) {
      setError(response.message)
      setIsSaving(false)
      return
    }

    await loadBrands()

    setIsSaving(false)
    closeModal()
  }

  const handleDelete = async (brand: VehicleBrand) => {
    const confirmed = await confirm({
      title: 'Delete Vehicle Brand',
      message: `Are you sure you want to delete "${brand.name}"? This action cannot be undone.`,
      confirmText: 'Delete Brand',
      cancelText: 'Cancel',
      variant: 'danger',
      icon: 'trash',
    })

    if (!confirmed) return

    const response = await apiDeleteVehicleBrand(brand.id)

    if (!response.success) {
      setError(response.message)
      return
    }

    await showSuccess({
      title: 'Brand Deleted',
      message: `"${brand.name}" has been deleted successfully.`,
    })

    await loadBrands()
  }

  const handleToggleActive = async (brand: VehicleBrand) => {
    const response = await apiUpdateVehicleBrand(brand.id, {
      name: brand.name,
      slug: brand.slug,
      is_active: !brand.is_active,
    })

    if (!response.success) {
      setError(response.message)
      return
    }

    await loadBrands()
  }

  return (
    <div className="rounded-[16px] bg-white p-[24px] shadow-sm">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-host text-[18px] font-bold text-[#1A1A1A]">
            Vehicle Brands
          </h2>

          <p className="mt-1 font-host text-[13px] text-[#757575]">
            Manage the vehicle manufacturers available in the system.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          className="flex items-center gap-2 rounded-[8px] bg-[#334E1C] px-4 py-2.5 font-host text-[13px] font-semibold text-white transition-colors hover:bg-[#253914]"
        >
          <Plus size={16} />
          Add Brand
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 font-host text-[13px] text-red-700">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="py-10 text-center font-host text-[14px] text-[#757575]">
          Loading brands...
        </div>
      ) : brands.length === 0 ? (
        <div className="rounded-[12px] border border-dashed border-[#D0D0D0] py-12 text-center">
          <p className="font-host text-[14px] text-[#757575]">
            No vehicle brands found.
          </p>

          <button
            type="button"
            onClick={openCreateModal}
            className="mt-3 font-host text-[13px] font-semibold text-[#334E1C]"
          >
            Create your first brand
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[650px] border-collapse">
            <thead>
              <tr className="border-b border-[#EEEEEE] bg-[#FAFAFA]">
                <th className="px-4 py-3 text-left font-host text-[12px] font-semibold text-[#757575]">
                  Name
                </th>

                <th className="px-4 py-3 text-left font-host text-[12px] font-semibold text-[#757575]">
                  Slug
                </th>

                <th className="px-4 py-3 text-left font-host text-[12px] font-semibold text-[#757575]">
                  Status
                </th>

                <th className="px-4 py-3 text-right font-host text-[12px] font-semibold text-[#757575]">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {brands.map((brand) => (
                <tr
                  key={brand.id}
                  className="border-b border-[#F0F0F0] last:border-b-0"
                >
                  <td className="px-4 py-4">
                    <span className="font-host text-[14px] font-semibold text-[#1A1A1A]">
                      {brand.name}
                    </span>
                  </td>

                  <td className="px-4 py-4">
                    <span className="font-host text-[13px] text-[#555555]">
                      {brand.slug}
                    </span>
                  </td>

                  <td className="px-4 py-4">
                    <button
                      type="button"
                      onClick={() => handleToggleActive(brand)}
                      className={`rounded-full px-3 py-1 font-host text-[11px] font-semibold ${
                        brand.is_active
                          ? 'bg-[#E8F5E9] text-[#2E7D32]'
                          : 'bg-[#F5F5F5] text-[#757575]'
                      }`}
                    >
                      {brand.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(brand)}
                        className="flex size-[32px] items-center justify-center rounded-[7px] border border-[#E0E0E0] text-[#666666] hover:bg-[#F7F7F7]"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(brand)}
                        className="flex size-[32px] items-center justify-center rounded-[7px] border border-[#E0E0E0] text-red-500 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-[520px] rounded-[16px] bg-white p-[24px] shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-host text-[18px] font-bold text-[#1A1A1A]">
                {editingBrand ? 'Edit Vehicle Brand' : 'Add Vehicle Brand'}
              </h3>

              <button
                type="button"
                onClick={closeModal}
                className="text-[#757575] hover:text-[#1A1A1A]"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label className="mb-1.5 block font-host text-[13px] font-medium text-[#1A1A1A]">
                  Brand Name
                </label>

                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g. Mercedes-Benz"
                  className="h-[44px] w-full rounded-[8px] border border-[#E0E0E0] px-4 font-host text-[14px] outline-none focus:border-[#FF6A1A]"
                />
              </div>

              <div>
                <label className="mb-1.5 block font-host text-[13px] font-medium text-[#1A1A1A]">
                  Slug
                </label>

                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      slug: e.target.value,
                    })
                  }
                  placeholder="e.g. mercedes-benz"
                  className="h-[44px] w-full rounded-[8px] border border-[#E0E0E0] px-4 font-host text-[14px] outline-none focus:border-[#FF6A1A]"
                />
              </div>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      is_active: e.target.checked,
                    })
                  }
                  className="size-4"
                />

                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  Active
                </span>
              </label>
            </div>

            <div className="mt-7 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-[8px] border border-[#E0E0E0] px-5 py-2.5 font-host text-[13px] font-semibold text-[#1A1A1A]"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="rounded-[8px] bg-[#334E1C] px-5 py-2.5 font-host text-[13px] font-semibold text-white disabled:opacity-50"
              >
                {isSaving
                  ? 'Saving...'
                  : editingBrand
                    ? 'Save Changes'
                    : 'Create Brand'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
